import React from 'react';
import PropTypes from 'prop-types';
import NextHead from 'next/head';

import i18n from '../../utils/i18n';
import { gtmHeadSnippet } from '../../utils/googleTagManager';

import socialMediaImage from './images/social_media.png';

import AlternateLinkTags from './AlternateLinkTags';
import CookieConsent from './CookieConsent';
import RelCanonical, { getCanonicalUrl } from './RelCanonical';

const Head = ({
  pageTitle = i18n.gettext('default_page_title'),
  ogTitle = 'Deliveroo',
  metaDescription = i18n.gettext(
    'Get amazing food from an incredible selection of local restaurants delivered in about 30 minutes.',
  ),
  metadata,
  ogDescription,
  image = {
    width: 1208,
    height: 630,
    src: socialMediaImage,
  },
  pagePath,
  alternateLocales,
  currentCountry,
  currentLocale,
  robots,
  isOneTrustCookieConsentEnabled,
}) => {
  // store the OneTrust feature flag status, so it can be checked
  // when setting cookies in utils/cookies/client.js
  if (typeof window !== 'undefined') {
    window.isOneTrustCookieConsentEnabled = isOneTrustCookieConsentEnabled;
  }

  return (
    <NextHead>
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="nosnippet" />
      <AlternateLinkTags pagePath={pagePath} alternates={alternateLocales} />
      <RelCanonical pagePath={pagePath} country={currentCountry} currentLocale={currentLocale} />

      <meta
        property="og:url"
        content={getCanonicalUrl({ pagePath, country: currentCountry, currentLocale })}
      />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription || metaDescription} />
      <meta property="og:image" content={image.src} />
      <meta property="og:image:width" content={image.width} />
      <meta property="og:image:height" content={image.height} />
      <meta property="twitter:title" content={ogTitle} />
      <meta property="twitter:site" content="@deliveroo" />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:description" content={ogDescription || metaDescription} />
      <meta property="twitter:image" content={image.src} />
      {metadata.length > 0 &&
        metadata.map(({ name, property, content }) => (
          <meta
            key={`${name}_${property}_${content}`}
            name={name}
            property={property}
            content={content}
          />
        ))}
      {robots && <meta name="robots" content={robots} />}
      {isOneTrustCookieConsentEnabled && (
        <CookieConsent tld={currentCountry.tld} locale={currentLocale} />
      )}
      {gtmHeadSnippet(isOneTrustCookieConsentEnabled)}
    </NextHead>
  );
};

Head.propTypes = {
  pageTitle: PropTypes.string,
  ogTitle: PropTypes.string,
  metaDescription: PropTypes.string,
  /**
   * To support arbitrary metadata tags, this component can also
   * accept an array of raw <meta> tags here.
   */
  metadata: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      property: PropTypes.string,
      content: PropTypes.string.isRequired,
    }),
  ),
  ogDescription: PropTypes.string,
  image: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    src: PropTypes.string.isRequired,
  }),
  pagePath: PropTypes.string.isRequired,
  alternateLocales: PropTypes.arrayOf(
    PropTypes.shape({
      hrefLang: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
  currentCountry: PropTypes.shape({
    host: PropTypes.string.isRequired,
    languages: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  }).isRequired,
  currentLocale: PropTypes.string,
  robots: PropTypes.string,
  isOneTrustCookieConsentEnabled: PropTypes.bool.isRequired,
};

Head.defaultProps = {
  pageTitle: undefined,
  ogTitle: undefined,
  metaDescription: undefined,
  metadata: [],
  ogDescription: undefined,
  image: undefined,
  alternateLocales: undefined,
  currentLocale: undefined,
  robots: undefined,
};

export default Head;
